import { Tab, Tabs } from '@mui/material';
import { Box } from '@mui/system';
import { useCallback, useMemo } from 'react';
import {
    Route,
    Routes,
    useLocation,
    useNavigate,
    useParams,
} from 'react-router-dom';
import { ReactComponent as SummaryIcon } from 'src/assets/svgs/bar-chart.svg';
import { Breadcrumbs } from 'src/components/Breadcrumbs';
import { DefaultButton } from 'src/components/DefaultButton';
import { PageHeading } from 'src/components/PageHeading';
import { PrivateRoute } from 'src/components/PrivateRoute';
import { useExportPDF, useSinglePatient } from 'src/hooks/PatientHook';
import { SinglePatientPageConsultation } from './SinglePatientPageConsultations';
import { SinglePatientPageDetails } from './SinglePatientPageDetails';
import { SinglePatientPageInsulinDosage } from './SinglePatientPageInsulinDosage';
import {
    pageHeadingSummaryButtonStyles,
    singlePatientTabStyles,
    singlePatientsTabContentStyles,
    singlePatientsTabPanelStyles,
} from './styles';

const singlePatientSubPages = [
  {
    label: 'Patient Details',
    url: '',
  },
  {
    label: 'Consultation History',
    url: '/consultation',
  },
  {
    label: 'Insulin Dosage',
    url: '/insulin-dosage',
  },
];

export function SinglePatientPage(): React.JSX.Element {
  const { patientId } = useParams<{ patientId: string }>();
  const [patient] = useSinglePatient({ patientId: patientId || '', fetchImmediately: !!patientId });
  const [exportPdf] = useExportPDF();
  const fullName = `${patient?.firstName} ${patient?.lastName}`;
  const navigate = useNavigate();
  const location = useLocation();

  const handleChangeTab = useCallback(
    (_, value: string) => {
      navigate(value);
    },
    [navigate]
  );

  const handleExport = useCallback(async (): Promise<void> => {
    if (!patientId) return;
    try {
      await exportPdf(patientId);
    } finally {
    }
  }, [exportPdf, patientId]);

  const parentPath = `/dashboard/patients/${patientId || ''}`;
  const currentParentUrl = parentPath;
  const currentPathname = location.pathname;

  const currentTab = useMemo(() => {
    const matchedTab =
      singlePatientSubPages.find(({ url }) =>
        currentPathname === `${parentPath}${url}`
      ) || singlePatientSubPages[0];

    return `${currentParentUrl}${matchedTab.url}`;
  }, [currentPathname, parentPath, currentParentUrl]);
  
  if (!patientId) {
    return <div>Patient not found</div>;
  }

  const breadcrumbs = (
    <Breadcrumbs
      links={[
        { label: 'Patients', url: '/dashboard' },
        { label: fullName || '' },
      ]}
    />
  );

  const actions = (
    <>
      <DefaultButton
        onClick={handleExport}
        sx={pageHeadingSummaryButtonStyles}
        variant="outlined"
      >
        <span>Patient Summary</span> <SummaryIcon />
      </DefaultButton>
      <DefaultButton to={`${currentParentUrl}/new-consultation`}>
        New Consultation
      </DefaultButton>
    </>
  );

  return (
    <>
      <PageHeading
        title={fullName}
        subheading={breadcrumbs}
        actions={actions}
      />
      <Box>
        <Box sx={singlePatientsTabPanelStyles}>
          <Tabs onChange={handleChangeTab} value={currentTab}>
            {singlePatientSubPages.map(({ label, url }) => (
              <Tab
                sx={singlePatientTabStyles}
                label={label}
                key={label}
                value={`${currentParentUrl}${url}`}
              />
            ))}
          </Tabs>
        </Box>
        <Box sx={singlePatientsTabContentStyles}>
          <Routes>
            <Route 
              path="/" 
              element={
                <PrivateRoute>
                  {patient && <SinglePatientPageDetails patient={patient} />}
                </PrivateRoute>
              } 
            />
            <Route
              path="/consultation"
              element={
                <PrivateRoute>
                  <SinglePatientPageConsultation />
                </PrivateRoute>
              }
            />
            <Route
              path="/insulin-dosage"
              element={
                <PrivateRoute>
                  <SinglePatientPageInsulinDosage />
                </PrivateRoute>
              }
            />
          </Routes>
        </Box>
      </Box>
    </>
  );
}
